/**
 * ResolvedConceptGroupChip — a single concept-group chip resolved by id via the
 * route-independent ``useGetConceptGroupDetailsQuery`` (OR-6018, WS-C).
 *
 * Each instance owns exactly one ``useGetConceptGroupDetailsQuery`` call (one
 * chip = one hook), so a variable-length list of ids resolves without breaking
 * the rules of hooks. RTK Query dedupes by arg, so multiple chips for the same
 * id share one request.
 *
 * Resolution is driven entirely by the id (callers only ever have the id — the
 * empty-mapping fallback and the table's broken-binding path). The chip
 * distinguishes a *genuine miss* from a *resolved group that simply has no
 * name*: a query error / 404 (``isError``) or a success that returns no group
 * renders the ``ConceptGroupChip`` UNAVAILABLE state (``clickable={false}``),
 * surfacing the broken binding; a success for a real group whose
 * ``conceptGroupName`` is null/empty is a valid (clickable) group shown with the
 * id as a sensible label rather than falsely reading as broken (finding #9).
 * While loading it pulses.
 *
 * The chip opens its group via ``onOpen(conceptGroupId)`` — id-driven, never by
 * name (so a stale / corrected label can't misroute the click).
 *
 * ``deferFetch`` (OR-6858): true while a sibling batch-resolve request (see
 * ``useBatchPrewarmConceptGroups``) is in flight — the chip's own query would
 * otherwise fire on mount, in the SAME render pass as the batch request,
 * defeating the batching. While deferred the chip renders its loading
 * (pulsing) state, matching what it would show if the query itself were
 * still loading; the underlying query is skipped, not merely delayed, so no
 * extra network call is dispatched. Once the batch settles and this flips to
 * false, the chip either hits the now-seeded cache (no request) or fires its
 * own request for the first time (a group the batch didn't resolve, e.g. an
 * unpublished draft).
 */

import { useIntl } from 'react-intl';

import { useGetConceptGroupDetailsQuery } from '@verantos-dev/data-access-concept-groups';
import { ConceptGroupChip } from '@verantos-dev/shared-components';

interface ResolvedConceptGroupChipProps {
  conceptGroupId: string;
  /** Owner to seed the chip icon before the lookup resolves. */
  fallbackOwner?: string;
  /**
   * OR-6335: a trusted display name. When set (the manifest name is GOOD but its
   * ``owner`` is missing), the chip shows this name as-is and resolves ONLY the
   * owner by id — it does not adopt the catalog name (finding #20: a good
   * manifest name is authoritative and must agree across chat link / replay /
   * Definition tab) and does not flag the group unavailable on a background
   * owner-lookup miss. Absent → the id drives both name and owner (the
   * broken-binding / empty-mapping path).
   */
  displayName?: string;
  /** Opens the group details — always by the structured concept-group id. */
  onOpen: (conceptGroupId: string) => void;
  maxWidth?: number | string;
  /** See the header doc comment. Defaults to false — fetch immediately. */
  deferFetch?: boolean;
}

export const ResolvedConceptGroupChip = ({
  conceptGroupId,
  fallbackOwner,
  displayName,
  onOpen,
  maxWidth,
  deferFetch = false,
}: ResolvedConceptGroupChipProps) => {
  const { formatMessage } = useIntl();
  const { data, isLoading: queryIsLoading } = useGetConceptGroupDetailsQuery(
    { conceptGroupId },
    { skip: deferFetch }
  );
  // A skipped query reports isLoading:false with no data — without this, the
  // chip would render UNAVAILABLE the instant it's deferred instead of pulsing.
  const isLoading = deferFetch || queryIsLoading;
  const group = data?.data;

  // Resolved → catalog owner wins; before that, only the seed owner exists.
  const owner = group?.owner ?? fallbackOwner;

  // OR-6335: owner-only resolution. The name is trusted as given; render it now
  // and let the catalog owner fill the icon when the lookup resolves. Always
  // clickable, opened by id — never flips to unavailable on an owner miss.
  const trustedName = displayName?.trim() || undefined;
  if (trustedName != null) {
    return (
      <ConceptGroupChip
        name={trustedName}
        owner={owner}
        onClick={() => onOpen(conceptGroupId)}
        maxWidth={maxWidth}
      />
    );
  }

  // An empty/whitespace catalog name is treated as "no name" (not as a blank
  // label) so an empty string never reaches the chip. `||` (not `??`) collapses
  // '' → undefined.
  const catalogName = group?.conceptGroupName?.trim() || undefined;

  // Resolved → catalog name wins; before that, only the seed owner exists.
  const resolvedName = catalogName;

  // No resolved catalog name yet → decide loading vs. resolved-empty vs.
  // genuinely-unavailable using the query state, not just a null name
  // (finding #9).
  if (resolvedName == null) {
    if (isLoading) {
      return <ConceptGroupChip name={conceptGroupId} loading maxWidth={maxWidth} />;
    }
    // The query SUCCEEDED for a real group (data present) that just has no
    // catalog name → a valid group, shown with the id as a sensible label and
    // still clickable. Distinguish this from a true miss below.
    if (group != null) {
      return (
        <ConceptGroupChip
          name={conceptGroupId}
          owner={owner}
          onClick={() => onOpen(conceptGroupId)}
          maxWidth={maxWidth}
        />
      );
    }
    // Genuine miss: query error / 404, or a success that returned no group →
    // surface the broken binding as the unavailable chip.
    return (
      <ConceptGroupChip
        name={conceptGroupId}
        clickable={false}
        unavailableTooltip={formatMessage({ id: 'epiChat.conceptGroupFallback.unavailable' })}
        maxWidth={maxWidth}
      />
    );
  }

  // Otherwise: a resolved catalog name — always clickable, opened by id.
  return (
    <ConceptGroupChip
      name={resolvedName}
      owner={owner}
      onClick={() => onOpen(conceptGroupId)}
      maxWidth={maxWidth}
    />
  );
};
